import React, { useState, useEffect } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import HeaderSelector from "./components/HeaderSelector";
import DevicesPage from "./pages/DevicesPage";
import MessagesPage from "./pages/MessagesPage";
import "./App.css";

function AppContent() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const ucParam = searchParams.get('uc');
    
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [playingUrl, setPlayingUrl] = useState(null);
    const [selectedSubDeviceUrl, setSelectedSubDeviceUrl] = useState(null);
    const [selectedSubDeviceLabel, setSelectedSubDeviceLabel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const devicesApiUrl = "/api/devices";
    const devicesJsonUrl = "/devices.json";

    const normalizeDevices = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (payload && Array.isArray(payload.devices)) return payload.devices;
        return null;
    };

    useEffect(() => {
        // Load devices from the JSON
        const applyDevices = (data) => {
            setDevices(data);
            if (Array.isArray(data) && data.length > 0) {
                const first = data[0];
                setSelectedDevice(first);
                const isQuad = first?.layout === "quad";
                const firstSub =
                    !isQuad &&
                        Array.isArray(first.subDevices) &&
                        first.subDevices.length
                        ? first.subDevices[0]
                        : null;
                const norm = (u) =>
                    typeof u === "string" ? u.replace(/\/+$/, "") : u;
                const initialUrl = norm(
                    isQuad
                        ? first?.url || firstSub?.url || ""
                        : firstSub?.url || first?.url || "",
                );
                setSelectedSubDeviceUrl(initialUrl);
                setSelectedSubDeviceLabel(
                    isQuad
                        ? first?.label || "4 streams"
                        : firstSub?.label || "main",
                );
            }
            setIsLoading(false);
        };

        const loadDevices = async () => {
            try {
                setIsLoading(true);
                setError(null);

                try {
                    const apiResponse = await fetch(devicesApiUrl, {
                        cache: "no-store",
                    });
                    if (!apiResponse.ok)
                        throw new Error("Failed to fetch devices from API");
                    const apiPayload = await apiResponse.json();
                    const apiDevices = normalizeDevices(apiPayload);
                    if (!apiDevices)
                        throw new Error("Invalid devices payload from API");
                    applyDevices(apiDevices);
                    return;
                } catch (apiError) {
                    console.warn(
                        "Device API unavailable, falling back to static devices.json",
                        apiError,
                    );
                    try {
                        const response = await fetch(devicesJsonUrl, {
                            cache: "no-store",
                        });
                        if (!response.ok)
                            throw new Error("Failed to fetch devices fallback");
                        const data = await response.json();
                        const fallbackDevices = normalizeDevices(data);
                        if (!fallbackDevices)
                            throw new Error(
                                "Invalid devices payload from fallback",
                            );
                        applyDevices(fallbackDevices);
                    } catch (fetchError) {
                        console.error(
                            "Could not load devices definition:",
                            fetchError,
                        );
                        setError(
                            fetchError.message || "Failed to load devices",
                        );
                        setIsLoading(false);
                    }
                }
            } catch (err) {
                console.error("Error loading devices:", err);
                setError(err.message);
                setIsLoading(false);
            }
        };

        loadDevices();
    }, []);

    const handleDeviceSelect = (device) => {
        const isQuad = device?.layout === "quad";
        const firstSubDevice =
            !isQuad &&
                Array.isArray(device?.subDevices) &&
                device.subDevices.length
                ? device.subDevices[0]
                : null;
        const norm = (u) => (typeof u === "string" ? u.replace(/\/+$/, "") : u);
        const newSelected = norm(
            isQuad
                ? device?.url || firstSubDevice?.url
                : firstSubDevice?.url || device?.url,
        );

        // ALWAYS stop playback when selecting a different device
        // The new device will be shown in preview mode (not playing)
        if (playingUrl) {
            setPlayingUrl(null);
        }

        setSelectedDevice(device);
        setSelectedSubDeviceUrl(newSelected);
        setSelectedSubDeviceLabel(
            isQuad
                ? device?.label || "4 streams"
                : firstSubDevice?.label || "main",
        );
    };

    // deviceOrUrl can be a full URL string (from subtype) or a device object
    const handleDevicePlay = (deviceOrUrl, deviceObj, label) => {
        const raw =
            typeof deviceOrUrl === "string" ? deviceOrUrl : deviceOrUrl?.url;
        if (!raw) return;
        const norm = raw.replace(/\/+$/, "");
        const targetDevice =
            typeof deviceOrUrl === "string" ? deviceObj : deviceOrUrl;

        setPlayingUrl(norm);
        if (targetDevice) setSelectedDevice(targetDevice);
        setSelectedSubDeviceUrl(norm);
        setSelectedSubDeviceLabel(label || "main");
    };

    const handleSubDeviceSelect = (url, device, label) => {
        const norm = (u) => (typeof u === "string" ? u.replace(/\/+$/, "") : u);
        const newSelected = norm(url);

        // ALWAYS stop playback when selecting a different sub-device
        // The new sub-device will be shown in preview mode (not playing)
        if (playingUrl) {
            setPlayingUrl(null);
        }

        setSelectedDevice(device);
        setSelectedSubDeviceUrl(newSelected);
        setSelectedSubDeviceLabel(label || "stream");
    };

    const handleStop = () => setPlayingUrl(null);

    const extractDeviceName = (url) => {
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split("/").filter(Boolean);
            return pathParts[pathParts.length - 1] || urlObj.hostname;
        } catch {
            return url;
        }
    };

    if (isLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    backgroundColor: "var(--color-white)",
                }}
            >
                <div style={{ textAlign: "center" }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "80px",
                            height: "80px",
                            backgroundColor: "var(--color-white)",
                            borderRadius: "16px",
                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                            marginBottom: "16px",
                        }}
                    >
                        <div
                            style={{
                                width: "40px",
                                height: "40px",
                                border: "4px solid var(--color-secondary)",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                            }}
                        ></div>
                    </div>
                    <p
                        style={{
                            fontSize: "16px",
                            color: "var(--color-background)",
                            fontWeight: "600",
                        }}
                    >
                        Loading devices...
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    backgroundColor: "var(--color-white)",
                }}
            >
                <div
                    style={{
                        textAlign: "center",
                        padding: "32px",
                        backgroundColor: "var(--color-white)",
                        borderRadius: "16px",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                        maxWidth: "448px",
                    }}
                >
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "64px",
                            height: "64px",
                            backgroundColor: "var(--color-alert)",
                            borderRadius: "50%",
                            marginBottom: "16px",
                        }}
                    >
                        <svg
                            style={{
                                width: "32px",
                                height: "32px",
                                color: "var(--color-white)",
                            }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <h2
                        style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "var(--color-background)",
                            marginBottom: "8px",
                        }}
                    >
                        Error Loading Devices
                    </h2>
                    <p
                        style={{
                            color: "var(--color-alert)",
                            fontWeight: "500",
                            fontSize: "14px",
                        }}
                    >
                        {error}
                    </p>
                </div>
            </div>
        );
    }

    // Determine current view from location
    const currentView =
        location.pathname === "/messages" ? "messages" : "devices";

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
            }}
        >
            <HeaderSelector currentView={currentView} />
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    backgroundColor: "var(--color-white)",
                    overflow: "hidden",
                }}
            >
                <Routes>
                    <Route
                        path="/devices"
                        element={
                            <DevicesPage
                                devices={devices}
                                selectedDevice={selectedDevice}
                                onDeviceSelect={handleDeviceSelect}
                                onDevicePlay={handleDevicePlay}
                                playingUrl={playingUrl}
                                selectedSubDeviceUrl={selectedSubDeviceUrl}
                                onSubDeviceSelect={handleSubDeviceSelect}
                                selectedSubDeviceLabel={selectedSubDeviceLabel}
                                onStop={handleStop}
                                extractDeviceName={extractDeviceName}
                            />
                        }
                    />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route
                        path="/"
                        element={
                            ucParam ? (
                                <Navigate to={`/messages?uc=${ucParam}`} replace />
                            ) : (
                                <Navigate to="/devices" replace />
                            )
                        }
                    />
                </Routes>
            </div>
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AppContent />
        </BrowserRouter>
    );
}

export default App;
